'use client'

import { Badge } from '@brew/ui/badge'
import { cn } from '@brew/ui/cn'
import { Bolt } from 'lucide-react'
import { ICON } from '@/lib/ui/icon'
import { TYPE } from '@/lib/ui/typography'

type TriggerableEvent = {
  eventType: string
  title: string
  description: string
  category: string
  /**
   * The first use case's title — surfaced as a tag so users can
   * quickly scan what each event powers (e.g. "Welcome email",
   * "Abandoned-cart Stage 1").
   */
  primaryUseCase?: string
}

export type TriggerableCategory = {
  id: string
  label: string
  events: ReadonlyArray<TriggerableEvent>
}

export type InboundEventsSectionProps = {
  providerName: string
  categories: ReadonlyArray<TriggerableCategory>
}

/**
 * Renders a "What events you can trigger automations from" section
 * for the integration page. Driven by the provider's presenter (or
 * Stripe's parallel catalog) so adding a new provider is one
 * presenter + a catalog entry — no per-provider UI code.
 */
export function InboundEventsSection({
  providerName,
  categories,
}: InboundEventsSectionProps) {
  const filtered = categories.filter((category) => category.events.length > 0)
  const totalEvents = filtered.reduce(
    (sum, category) => sum + category.events.length,
    0
  )

  if (totalEvents === 0) {
    return null
  }

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <p className={TYPE.kicker}>Triggers</p>
        <h2 className={TYPE.heading}>
          What events you can trigger automations from
        </h2>
        <p className={cn(TYPE.bodyMuted, 'max-w-[64ch] leading-relaxed')}>
          Once {providerName} is connected, each event below becomes a Brew
          trigger. A trigger starts its published automations only when{' '}
          {providerName} sends Brew that event.
        </p>
      </div>
      <div className="flex flex-col gap-6">
        {filtered.map((category) => (
          <div key={category.id} className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className={TYPE.bodyMedium}>{category.label}</h3>
              <Badge variant="secondary" className="text-caption uppercase">
                {category.events.length}{' '}
                {category.events.length === 1 ? 'event' : 'events'}
              </Badge>
            </div>
            <ul className="flex flex-col gap-3">
              {category.events.map((event) => (
                <li
                  key={event.eventType}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-4',
                    'sm:flex-row sm:items-start sm:gap-4'
                  )}
                >
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
                    <Bolt
                      className={cn(
                        ICON.default,
                        'text-emerald-600 dark:text-emerald-400'
                      )}
                      aria-hidden
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={cn(TYPE.bodyMedium, 'leading-tight')}>
                        {event.title}
                      </p>
                      {event.primaryUseCase ? (
                        <Badge
                          variant="outline"
                          className="text-caption uppercase"
                        >
                          {event.primaryUseCase}
                        </Badge>
                      ) : null}
                    </div>
                    <p className={cn(TYPE.supporting, 'leading-relaxed')}>
                      {event.description}
                    </p>
                    <code className={cn(TYPE.caption, 'mt-1 font-mono')}>
                      {event.eventType}
                    </code>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}
